"use client";

import { PriceAnchor } from "@/components/services/price-anchor";
import { useMemo, useState, type ReactNode } from "react";
import Link from "next/link";
import { m } from "framer-motion";
import {
  Globe2,
  Calendar,
  ArrowRight,
  Check,
  AlertTriangle,
  Lock,
  ShieldCheck,
  RefreshCw,
  KeyRound,
  FileSearch,
  ArrowLeftRight,
  BellRing,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────
 * Hero-Mockup: Domain-Portfolio-Karte
 * ──────────────────────────────────────────────────────────── */

function DomainPortfolioCard() {
  const rows = [
    { domain: "ihre-firma.de", status: "Aktiv", expiry: "Auto-Verlängerung", ok: true },
    { domain: "ihre-firma.com", status: "Aktiv", expiry: "Auto-Verlängerung", ok: true },
    { domain: "ihre-marke.de", status: "Aktiv", expiry: "Auto-Verlängerung", ok: true },
  ];
  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-5 sm:p-6"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--card-bg)",
        boxShadow: "var(--card-shadow-lg)",
      }}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-400/15 blur-3xl" />

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          <p className="text-xs font-semibold" style={{ color: "var(--text)" }}>
            Domain-Portfolio · alles im Griff
          </p>
        </div>
        <span
          className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
        >
          Inhaber: Sie
        </span>
      </div>

      <div className="relative mt-4 space-y-2">
        {rows.map((r) => (
          <div
            key={r.domain}
            className="flex items-center justify-between rounded-xl border px-3.5 py-2.5"
            style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)" }}
          >
            <div className="flex items-center gap-2.5">
              <Globe2 className="h-4 w-4 text-brand-500" />
              <span className="text-sm font-medium" style={{ color: "var(--text)" }}>
                {r.domain}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden text-xs sm:inline" style={{ color: "var(--text-quaternary)" }}>
                {r.expiry}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold" style={{ color: "var(--fi-success-fg)" }}>
                <Check className="h-3 w-3" />
                {r.status}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="relative mt-4 grid grid-cols-3 gap-2 text-center">
        {[
          { label: "DNS", value: "Gepflegt" },
          { label: "SSL", value: "Gültig" },
          { label: "Ablauf", value: "Überwacht" },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-xl border px-2 py-2.5"
            style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)" }}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--text-quaternary)" }}>
              {s.label}
            </p>
            <p className="mt-0.5 text-xs font-semibold text-brand-500">{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Interaktiver Domain-Risiko-Check
 * 4 Ja/Nein-Fragen → Risiko-Ampel. Psychologie: Die meisten
 * Inhaber kleiner Unternehmen können 2 der 4 Fragen nicht mit
 * „Ja" beantworten – der Moment, in dem das Problem real wird.
 * ──────────────────────────────────────────────────────────── */

const CHECK_QUESTIONS = [
  {
    q: "Wissen Sie, bei welchem Anbieter Ihre Domain registriert ist?",
    hint: "Registrar = der Anbieter, bei dem die Domain liegt (z. B. ein Hoster).",
  },
  {
    q: "Sind SIE selbst als Domain-Inhaber eingetragen – nicht Ihre Agentur?",
    hint: "Steht die Agentur als Inhaber drin, gehört Ihnen die Domain rechtlich nicht.",
  },
  {
    q: "Haben Sie eigene Zugangsdaten zur Domain-Verwaltung?",
    hint: "Ohne Login können Sie weder umziehen noch DNS ändern.",
  },
  {
    q: "Wird die Domain automatisch verlängert und überwacht?",
    hint: "Abgelaufene Domains werden binnen Stunden von Dritten registriert.",
  },
] as const;

function RiskCheck() {
  // null = unbeantwortet, true = ja, false = nein
  const [answers, setAnswers] = useState<(boolean | null)[]>(
    Array(CHECK_QUESTIONS.length).fill(null),
  );

  const answered = answers.filter((a) => a !== null).length;
  const yesCount = answers.filter((a) => a === true).length;
  const done = answered === CHECK_QUESTIONS.length;

  const verdict = useMemo(() => {
    if (!done) return null;
    if (yesCount === 4)
      return {
        color: "#10b981",
        bg: "rgba(16,185,129,0.1)",
        title: "Gut aufgestellt",
        text: "Ihre Domain-Situation sieht solide aus. Wenn Sie die laufende Pflege (DNS, SSL, Überwachung) abgeben möchten, übernehmen wir das gern.",
      };
    if (yesCount >= 2)
      return {
        color: "#f59e0b",
        bg: "rgba(245,158,11,0.1)",
        title: "Lücken vorhanden",
        text: `${4 - yesCount} von 4 Punkten sind ungeklärt. Jeder davon kann im Ernstfall Wochen kosten – etwa wenn die Website umziehen soll oder die Domain ausläuft. Wir klären das in einem kurzen Gespräch.`,
      };
    return {
      color: "#ef4444",
      bg: "rgba(239,68,68,0.1)",
      title: "Hohes Risiko",
      text: "Ihre Domain ist faktisch nicht unter Ihrer Kontrolle. Falls der bisherige Dienstleister ausfällt oder sich querstellt, verlieren Sie schlimmstenfalls Domain, E-Mails und Website gleichzeitig. Das sollten wir schnell beheben.",
    };
  }, [done, yesCount]);

  return (
    <div
      className="rounded-2xl border p-5 lg:p-6"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--card-bg)",
        boxShadow: "var(--card-shadow)",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-lg border"
          style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)" }}
        >
          <FileSearch className="h-5 w-5 text-brand-500" />
        </div>
        <div>
          <h3 className="text-lg font-semibold" style={{ color: "var(--text)" }}>
            Der 60-Sekunden-Domain-Check
          </h3>
          <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>
            4 Fragen, ehrliche Antworten – mehr braucht es nicht.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {CHECK_QUESTIONS.map((item, i) => (
          <div
            key={i}
            className="rounded-xl border p-4"
            style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)" }}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--text)" }}>
                  {item.q}
                </p>
                <p className="mt-1 text-xs" style={{ color: "var(--text-quaternary)" }}>
                  {item.hint}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                {([true, false] as const).map((val) => {
                  const selected = answers[i] === val;
                  return (
                    <button
                      key={String(val)}
                      type="button"
                      onClick={() =>
                        setAnswers((prev) => {
                          const next = [...prev];
                          next[i] = val;
                          return next;
                        })
                      }
                      className="rounded-lg border px-4 py-1.5 text-sm font-semibold transition-all duration-150"
                      style={
                        selected
                          ? val
                            ? {
                                backgroundColor: "rgba(16,185,129,0.12)",
                                borderColor: "#10b981",
                                color: "#059669",
                              }
                            : {
                                backgroundColor: "rgba(239,68,68,0.12)",
                                borderColor: "#ef4444",
                                color: "#dc2626",
                              }
                          : {
                              borderColor: "var(--border)",
                              color: "var(--text-secondary)",
                              backgroundColor: "var(--card-bg)",
                            }
                      }
                    >
                      {val ? "Ja" : "Nein / unsicher"}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fortschritt */}
      {!done && (
        <p className="mt-4 text-center text-xs" style={{ color: "var(--text-quaternary)" }}>
          {answered} von {CHECK_QUESTIONS.length} beantwortet
        </p>
      )}

      {/* Ergebnis */}
      {verdict && (
        <m.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-xl border p-5"
          style={{ borderColor: verdict.color, backgroundColor: verdict.bg }}
        >
          <div className="flex items-center gap-2">
            {yesCount === 4 ? (
              <ShieldCheck className="h-5 w-5" style={{ color: verdict.color }} />
            ) : (
              <AlertTriangle className="h-5 w-5" style={{ color: verdict.color }} />
            )}
            <p className="font-semibold" style={{ color: verdict.color }}>
              {verdict.title} · {yesCount}/4
            </p>
          </div>
          <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {verdict.text}
          </p>
          <Link
            href="/beratung"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-150 hover:bg-brand-600"
          >
            <Calendar className="h-4 w-4" />
            Kostenlos prüfen lassen
          </Link>
        </m.div>
      )}
    </div>
  );
}

/** Server-gerenderte Abschnitte des Seitenschemas (Auftrag 2026-10-28, Paket 7), vom page.tsx übergeben. */
export type ServiceSlots = {
  afterHero?: ReactNode;
  afterPrice?: ReactNode;
  beforeProcess?: ReactNode;
  beforeCta?: ReactNode;
};

export function DomainsClient({ afterHero, afterPrice, beforeProcess, beforeCta }: ServiceSlots) {
  return (
    <>
      <nav className="mx-auto max-w-7xl px-4 py-4 lg:px-8" aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-2 text-sm" style={{ color: "var(--text-tertiary)" }}>
          <li><Link href="/" className="transition-colors hover:text-brand-500">Startseite</Link></li>
          <li aria-hidden="true">/</li>
          <li><Link href="/dienstleistungen" className="transition-colors hover:text-brand-500">Dienstleistungen</Link></li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="font-medium" style={{ color: "var(--text)" }}>Domain-Verwaltung</li>
        </ol>
      </nav>

      {/* ── Hero ── */}
      <section className="py-12 lg:py-20" style={{ backgroundColor: "var(--bg)" }}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <span
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium"
                style={{
                  color: "var(--text-brand)",
                  borderColor: "var(--border)",
                  backgroundColor: "var(--bg)",
                }}
              >
                <Globe2 className="h-3.5 w-3.5" />
                Domain-Verwaltung
              </span>
              <h1
                className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl"
                style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
              >
                Ihre Domain ist Ihr{" "}
                <span className="bg-gradient-to-r from-brand-400 to-brand-600 bg-clip-text text-transparent">
                  digitales Grundstück
                </span>
              </h1>
              <p className="mt-5 text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                Wir erwerben, verwalten und pflegen Ihre Domains – mit einem
                entscheidenden Unterschied: <strong>Sie sind und bleiben der
                Inhaber.</strong> Keine Abhängigkeit, kein Kleingedrucktes,
                volle Kontrolle.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  href="/beratung"
                  className="flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md"
                >
                  <Calendar className="h-5 w-5" />
                  Kostenloses Erstgespräch
                </Link>
                <a
                  href="#risiko-check"
                  className="flex items-center justify-center gap-2 rounded-lg border px-5 py-3 text-base font-semibold transition-all duration-150 hover:shadow-sm"
                  style={{
                    color: "var(--text-secondary)",
                    borderColor: "var(--border-secondary)",
                    backgroundColor: "var(--bg)",
                  }}
                >
                  Zum Domain-Check
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            <DomainPortfolioCard />
          </div>
        </div>
      </section>

      {afterHero}

      {/* Preisanker – Texte aus src/lib/facts.ts (PRICE_ANCHORS), bewusst ohne Beträge (Entscheidung 2026-10-21) */}
      <PriceAnchor service="domains" />
      {afterPrice}

      {/* ── Pain: Wem gehört Ihre Domain? ── */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold" style={{ color: "var(--text-brand)" }}>
              Das unterschätzte Risiko
            </p>
            <h2
              className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              Wem gehört eigentlich Ihre Domain?
            </h2>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
              Viele Unternehmen wissen es nicht genau – und merken es erst,
              wenn es zu spät ist. Drei Szenarien, die wir regelmäßig sehen:
            </p>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Lock,
                title: "Die Agentur hält die Domain",
                text: "Die alte Agentur hat die Domain auf sich registriert. Beim Anbieterwechsel beginnt das Feilschen – manchmal um vierstellige Beträge, manchmal blockiert die Agentur den Umzug monatelang.",
              },
              {
                icon: BellRing,
                title: "Die Domain läuft still aus",
                text: "Die Rechnung ging an ein altes E-Mail-Postfach, niemand hat verlängert. Nach Ablauf greifen Domain-Händler binnen Stunden zu – und Website plus E-Mails sind offline.",
              },
              {
                icon: KeyRound,
                title: "Niemand hat die Zugänge",
                text: "Der Mitarbeiter, der die Domain mal eingerichtet hat, ist weg. Login unbekannt, E-Mail-Adresse tot, Anbieter unklar. Jede DNS-Änderung wird zur Detektivarbeit.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-2xl border p-6"
                  style={{
                    backgroundColor: "var(--card-bg)",
                    borderColor: "var(--border)",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-lg"
                    style={{ backgroundColor: "rgba(239,68,68,0.1)" }}
                  >
                    <Icon className="h-5 w-5 text-red-500" />
                  </div>
                  <h3 className="mt-4 text-base font-semibold" style={{ color: "var(--text)" }}>
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Risiko-Check (interaktiv) ── */}
      <section id="risiko-check" className="scroll-mt-24 py-12 lg:py-16" style={{ backgroundColor: "var(--bg)" }}>
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <RiskCheck />
        </div>
      </section>

      {/* ── Leistungen: Erwerb / Verwaltung / Pflege ── */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold" style={{ color: "var(--text-brand)" }}>
              Unsere Leistung
            </p>
            <h2
              className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              Erwerb, Verwaltung und Pflege – aus einer Hand
            </h2>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Globe2,
                title: "Erwerb",
                items: [
                  "Beratung zu Name & Endungen",
                  "Verfügbarkeits-Recherche",
                  "Registrierung auf IHREN Namen",
                  "Schutz-Domains gegen Nachahmer",
                ],
              },
              {
                icon: RefreshCw,
                title: "Verwaltung",
                items: [
                  "Ablauf-Überwachung & Auto-Verlängerung",
                  "Inhaber-Daten aktuell halten",
                  "Domain-Umzüge (Transfer)",
                  "Zentrale Übersicht aller Domains",
                ],
              },
              {
                icon: ShieldCheck,
                title: "Pflege",
                items: [
                  "DNS-Verwaltung (Website, E-Mail, Dienste)",
                  "SPF, DKIM & DMARC für E-Mail-Zustellung",
                  "SSL-Zertifikate automatisch erneuert",
                  "Sicherheits-Checks der Einträge",
                ],
              },
            ].map((block) => {
              const Icon = block.icon;
              return (
                <div
                  key={block.title}
                  className="rounded-2xl border p-5 lg:p-6"
                  style={{
                    backgroundColor: "var(--card-bg)",
                    borderColor: "var(--border)",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-lg border"
                    style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)" }}
                  >
                    <Icon className="h-5 w-5 text-brand-500" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold" style={{ color: "var(--text)" }}>
                    {block.title}
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {block.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2 text-sm"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Inhaber-Versprechen */}
          <div
            className="mx-auto mt-8 max-w-3xl rounded-2xl border p-6 text-center lg:p-8"
            style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}
          >
            <p className="text-base font-semibold" style={{ color: "var(--fi-brand-fg)" }}>
              Unser Inhaber-Versprechen
            </p>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Jede Domain, die wir für Sie registrieren, läuft auf <strong>Ihren
              Namen</strong>. Sie erhalten alle Zugangsdaten dokumentiert und
              können jederzeit ohne unsere Zustimmung wechseln. So arbeiten wir –
              weil Vertrauen besser bindet als Abhängigkeit.
            </p>
          </div>
        </div>
      </section>

      {beforeProcess}

      {/* ── Ablauf (grau) ── */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold" style={{ color: "var(--text-brand)" }}>
              So läuft es ab
            </p>
            <h2
              className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              In drei Schritten zur geordneten Domain-Situation
            </h2>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[
              {
                step: "1",
                icon: FileSearch,
                title: "Bestandsaufnahme",
                text: "Wir prüfen gemeinsam: Welche Domains gibt es, wo liegen sie, wer ist eingetragen, was läuft wann ab? Das Ergebnis ist eine klare Übersicht – oft die erste überhaupt.",
              },
              {
                step: "2",
                icon: ArrowLeftRight,
                title: "Ordnung schaffen",
                text: "Fehlende Domains registrieren wir, bestehende ziehen wir bei Bedarf um, Inhaber-Daten werden korrigiert. Website und E-Mails bleiben dabei durchgehend online.",
              },
              {
                step: "3",
                icon: ShieldCheck,
                title: "Laufende Pflege",
                text: "Ab dann überwachen wir Ablaufdaten, pflegen DNS-Einträge und erneuern Zertifikate. Sie bekommen von alldem nichts mit – und genau das ist der Punkt.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.step}
                  className="relative rounded-2xl border p-5 lg:p-6"
                  style={{
                    backgroundColor: "var(--card-bg)",
                    borderColor: "var(--border)",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <span
                    className="absolute right-5 top-4 text-5xl font-bold opacity-10"
                    style={{ color: "var(--text)" }}
                  >
                    {item.step}
                  </span>
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-lg border"
                    style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)" }}
                  >
                    <Icon className="h-5 w-5 text-brand-500" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold" style={{ color: "var(--text)" }}>
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {beforeCta}

      {/* ── CTA ── */}
      <section className="py-12 lg:py-16" style={{ backgroundColor: "var(--bg)" }}>
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
          <h2
            className="text-2xl font-semibold tracking-tight sm:text-3xl"
            style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
          >
            Unsicher, wie Ihre Domain-Situation aussieht?
          </h2>
          <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
            Im kostenlosen Erstgespräch machen wir die Bestandsaufnahme –
            danach wissen Sie genau, wem was gehört und was zu tun ist.
            Unverbindlich und ohne Fachchinesisch.
          </p>
          <Link
            href="/beratung"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md"
          >
            <Calendar className="h-5 w-5" />
            Kostenloses Erstgespräch buchen
          </Link>
        </div>
      </section>
    </>
  );
}
